import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex } from 'rebass/styled-components';
import { Input, Loader } from '@getro/rombo';
import { Plus } from 'lucide-react';
import { capitalize } from 'lodash';
import { ScrollBox } from 'components/atoms/ScrollBox';

const ActionsSearch = ({
  value,
  title,
  onSearch,
  options,
  isLoading,
  onSelect,
  onCreateIfNotExists,
  isCreating,
  newItemText,
  showInputOnEmptyList,
}) => {
  const ref = useRef(null);
  const [initialOptionLength, setInitialOptionLength] = useState(options.length);

  const loader = (isLoading || isCreating) && (
    <Flex
      sx={{
        height: '100%',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        '& > div': { width: '24px', height: '24px' },
        background: 'transparent !important',
      }}
    >
      <Loader />
    </Flex>
  );

  useEffect(() => {
    setInitialOptionLength((previous) => {
      if (!previous) {
        return options.length;
      }
      return previous;
    });
  }, [options]);

  useEffect(
    () => () => {
      onSearch('');
    },
    [onSearch],
  );

  const isEmpty = !value && !initialOptionLength;

  return (
    <Flex ref={ref} sx={{ flexDirection: 'column', gap: '8px', height: '100%' }}>
      {((ref.current && !isCreating) || (options.length && !isLoading && !isCreating)) && (
        <>
          <Box as="h4" fontSize="14px" fontWeight="500" lineHeight="1.4">
            {title}
          </Box>
          {(showInputOnEmptyList || (!showInputOnEmptyList && initialOptionLength > 0)) && (
            <Input
              autoFocus
              size="small"
              placeholder={isEmpty ? `${capitalize(newItemText)} name` : 'Search'}
              p="8px 12px"
              value={value}
              onChange={(e) => onSearch(e.target.value)}
            />
          )}
        </>
      )}

      {!isCreating && (
        <ScrollBox
          sx={{
            flexDirection: 'column',
            flex: 1,
            gap: '6px',
            overflowY: 'scroll',
            height: 'calc(100% - 32px)',
            background: 'transparent !important',
          }}
        >
          {options.map((item) => (
            <Flex
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect?.(item)}
              key={item.label}
              sx={{
                height: '22px',
                alignItems: 'center',
              }}
              onClick={() => onSelect?.(item)}
            >
              <Box
                as="p"
                key={item.label}
                sx={{
                  color: 'text.main',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '14px',
                  ':first-letter': {
                    textTransform: 'capitalize',
                  },
                }}
              >
                {item.label}
              </Box>
            </Flex>
          ))}
          {options.length === 0 && (
            <Flex
              sx={{
                mt: '24px',
                justifyContent: 'center',
                flexDirection: 'column',
                alignItems: 'center',
                color: 'text.subtle',
                fontSize: '14px',
                lineHeight: '22px',
                fontStyle: 'italic',
              }}
            >
              No results
            </Flex>
          )}
        </ScrollBox>
      )}
      {((!value && !ref.current) || isCreating || value) && loader}

      {onCreateIfNotExists && !(isLoading || isCreating) && (
        <Flex
          onClick={() => onCreateIfNotExists(value)}
          sx={{
            minHeight: '40px',
            alignItems: 'center',
            gap: '8px',
            borderTopColor: '#DEE4ED !important',
            borderTop: '1px solid',
            pt: '8px',
            background: 'transparent !important',
          }}
        >
          <Box as={Plus} size="17px" sx={{ color: 'text.main' }} />
          <Box
            as="p"
            sx={{
              color: 'text.main',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '14px',
              fontWeight: '500',
              lineHeight: '20px',
              ':first-letter': {
                textTransform: 'capitalize',
              },
            }}
          >
            {newItemText} {value ? `"${value}"` : ''}
          </Box>
        </Flex>
      )}
    </Flex>
  );
};

ActionsSearch.propTypes = {
  value: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.any.isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onCreateIfNotExists: PropTypes.func,
  isCreating: PropTypes.bool,
  newItemText: PropTypes.string,
  showInputOnEmptyList: PropTypes.bool,
};

ActionsSearch.defaultProps = {
  onCreateIfNotExists: null,
  isCreating: false,
  newItemText: '',
  showInputOnEmptyList: true,
};

export default ActionsSearch;
